// NATIVE 
import * as React from 'react';

// INSTALLED 
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import EditIcon from '@mui/icons-material/Edit';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import DeleteIcon from '@mui/icons-material/Delete';
import Menu, { MenuProps } from '@mui/material/Menu';
import { Box, Card, Chip, Pagination, TablePagination } from '@mui/material';
import { styled, alpha  } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import TableContainer from '@mui/material/TableContainer';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import VisibilityIcon from '@mui/icons-material/Visibility';



// CUSTOM 
import { SnackAlert } from '../SnackAlert';
import { paddingCardsTables, timeToClosedToast } from '../../commons';
import MultipleSnackbars from '../MultipleSnackbars';
import { showNumberRowsTable } from '../../commons/utils/values';
import { ModalSessionExpired } from '../generics/ModalSessionExpired';
import { closeSessionGlobal, formatDateToYmd, hasPemissions } from '../../commons/utils';
import { EditDialogNovedadesTurnos } from '../modals/novedadesTurnos/EditDialogNovedadesTurnos';
import {  getTipoNovedadByPagination, storeDesActiveTipoNovedad } from '../../store/tipo_novedad';
import { DetailDialogNovedadesTurnos } from '../modals/novedadesTurnos/DetailDialogNovedadesTurnos';
import { getNovedadesTurnoStore, storeActiveNovedadTurno, storeDesActiveNovedadesTurno, storeDetailEditNovedadTurno, storeDetailNovedadTurno } from '../../store/novedadesTurnos';





const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: theme.palette.secondary.contrastText,
    fontSize: 15,
    fontWeight: 700
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontWeight: 500,
    color: '#2e263de6'
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));





/**
 * TableNovedadesTurno component displays a table of "novedades turno" (shift news) with pagination and various actions.
 * 
 * @component
 * 
 * @returns {JSX.Element} The rendered component.
 * 
 * @description
 * This component fetches and displays a list of "novedades turno" from the Redux store. It includes functionalities for viewing details, editing, deactivating, and restoring "novedades turno". The component also handles permissions for these actions.
 * 
 * @example
 * <TableNovedadesTurno />
 * 
 * @requires
 * - `useDispatch` from 'react-redux'
 * - `useSelector` from 'react-redux'
 * - `React` from 'react'
 * - `styled` from '@mui/material/styles'
 * - `Menu`, `MenuItem`, `Button`, `Chip`, `Card`, `TableContainer`, `Paper`, `Table`, `TableHead`, `TableRow`, `TableCell`, `TableBody`, `TablePagination`, `Box`, `Pagination` from '@mui/material'
 * - `KeyboardArrowDownIcon`, `VisibilityIcon`, `EditIcon`, `DeleteIcon` from '@mui/icons-material'
 * 
 * @state {Array} dataTableList - List of "novedades turno" to be displayed in the table.
 * @state {Object} pagination - Pagination details for the table.
 * @state {number} page - Current page number for the table.
 * @state {number} rowsPerPage - Number of rows per page for the table.
 * @state {number} pagePagination - Current page number for the pagination component.
 * @state {number} itemCountPagination - Total number of pages for the pagination component.
 * @state {number} timeAlert - Duration for alert messages.
 * @state {Array} messagesAlerts - List of alert messages.
 * @state {Array} msgErrors - List of error messages.
 * @state {HTMLElement | null} openElem - Currently open element for the menu.
 * @state {HTMLElement | null} anchorEl - Anchor element for the menu.
 * @state {string} message_alert_snack - Message for the snackbar alert.
 * @state {string} type_alert_snack - Type of the snackbar alert (e.g., 'success', 'error').
 * @state {boolean} open_cargo - State for opening the cargo modal.
 * @state {boolean} modalDetalleNovedadTurno - State for opening the detail modal.
 * @state {boolean} modalEditarNovedadTurno - State for opening the edit modal.
 * @state {boolean} modalSessionExpired - State for opening the session expired modal.
 * @state {boolean} canEdit - Permission state for editing.
 * @state {boolean} canDelete - Permission state for deleting.
 * @state {boolean} canRestore - Permission state for restoring.
 * @state {boolean} alert_snack - State for opening the snackbar alert.
 * @state {Object} msgSnack - Message object for the snackbar alert.
 * 
 * @function getDataParameters - Fetches necessary data parameters.
 * @function handleClick - Handles the click event for opening the menu.
 * @function handleClose - Handles the close event for the menu.
 * @function openDialogDetalleNovedadTurno - Opens the detail dialog for a specific "novedad turno".
 * @function openDialogEditarNovedadTurno - Opens the edit dialog for a specific "novedad turno".
 * @function desactiveNovedadTurno - Deactivates a specific "novedad turno".
 * @function restoreNovedadTurno - Restores a specific "novedad turno".
 * @function handleCloseModalEditarNovedadesTurnos - Closes the edit modal.
 * @function handleCloseModalDetalleNovedadTurno - Closes the detail modal.
 * @function handleChangeRowsPerPage - Handles the change event for rows per page.
 * @function handleChangePage - Handles the change event for page number.
 * @function changePaginationPage - Handles the change event for pagination page.
 * @function validateLsPermissions - Validates local storage permissions.
 * @function validateResponse - Validates the response from API calls.
 * @function checkEdit - Checks if the user has edit permissions.
 * @function checkCanDelete - Checks if the user has delete permissions.
 * @function checkRestore - Checks if the user has restore permissions.
 */
export const TableNovedadesTurno = () => {

    
    // DISPATCH 
    const dispatch = useDispatch();

  
    // REDUX
    const  { novedades_turno_list }:any  = useSelector( (state:any) => state.novedadesTurno );
    



    // USE STATE 
    const [dataTableList, setDataTable] = React.useState([]);
    const [pagination, setPagination] = React.useState<any>({
        "page": 1,
        "take": 1,
        "itemCount": 1,
        "pageCount": 1,
        "hasPreviousPage": false,
        "hasNextPage": false
    });


    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(showNumberRowsTable);
    const [pagePagination, setPagePagination] = React.useState(1);
    const [itemCountPagination, setItemCountPagination] = React.useState(1);


    const [timeAlert, setTimeAlert] = React.useState(timeToClosedToast);
    const [messagesAlerts, setMessagesAlerts] = React.useState<any>([]);


    const [msgErrors, setMsgErrors] = React.useState([]);

    const [openElem, setOpenElem] = React.useState(null);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);


    const [message_alert_snack, setMessageAlertSnack] = React.useState('');
    const [type_alert_snack, setTypeAlertSnack] = React.useState('success');


    
    const [open_cargo, setOpenCargo]     = React.useState(false);
    const [modalDetalleNovedadTurno, setModalDetalleNovedadTurno] = React.useState(false);
    const [modalEditarNovedadTurno, setModalEditarNovedadesTurnos] = React.useState(false);


    const [modalSessionExpired, setModalSessionExpired] = React.useState<boolean>(false);


    const [canEdit, setCanEdit]     = React.useState<boolean>(false);
    const [canDelete, setCanDelete]     = React.useState<boolean>(false);
    const [canRestore, setCanRestore]     = React.useState<boolean>(false);


    const [alert_snack, setAlertSnack] = React.useState(false);
    const [msgSnack, setMsgSnack] = React.useState({
        msg: '',
        status: 'success'
    });

    

    // USE EFFECT 
    React.useEffect(() => {
      
      if ( novedades_turno_list.data && novedades_turno_list.data.length > 0 ) {

        setDataTable(novedades_turno_list.data);
        setPagination({
          "page": novedades_turno_list.meta.page,
          "take": novedades_turno_list.meta.take,
          "itemCount": novedades_turno_list.meta.itemCount,
          "pageCount": novedades_turno_list.meta.pageCount,
          "hasPreviousPage": novedades_turno_list.meta.hasPreviousPage,
          "hasNextPage": novedades_turno_list.meta.hasNextPage
        });
        setItemCountPagination(novedades_turno_list.meta.pageCount);
        
      }
      

    }, [novedades_turno_list]);


    // HERE LOAD THE DATA NECESARY FOR CREATE PARAMETERS 
    React.useEffect( () => {
        getDataParameters();
    }, []);



    React.useEffect(() => {
      checkEdit()
    }, []);
  
    React.useEffect(() => {
        checkCanDelete()
    }, []);
  
    React.useEffect(() => {
      checkRestore()
    }, []);


    // CONST STYLES
    const StyledMenu = styled((props: MenuProps) => (
      <Menu
        elevation={0}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        {...props}
      />
    ))(({ theme }) => ({
      '& .MuiPaper-root': {
        borderRadius: 6,
        marginTop: theme.spacing(1),
        minWidth: 180,
        color:
          theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
        boxShadow:
          'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
        '& .MuiMenu-list': {
          padding: '7px 0',
        },
        '& .MuiMenuItem-root': {
          '& .MuiSvgIcon-root': {
            fontSize: 18,
            color: theme.palette.text.secondary,
            marginRight: theme.spacing(1.5),
          },
          '&:active': {
            backgroundColor: alpha(
              theme.palette.primary.main,
              theme.palette.action.selectedOpacity,
            ),
          },
        },
      },
    }));
    
    
    const Div = styled('div')(({ theme }) => ({
      ...theme.typography.button,
      backgroundColor: 'wheat',
      padding: theme.spacing(8),
      textAlign: 'center'
    }));

  

    // CONST VALUE
    const open = Boolean(anchorEl);
  
  

    // CONST FUNCTIONS

    const getDataParameters = async() => {



      // modulo.novedadesturnos                             nombre modulo     sub modulo    permission
      const havePermissions = await hasPemissions('modulo.thumano', 'modulo.novedades', 'view', 'modulo.novedadesturnos');


      if( !havePermissions ){


        await validateLsPermissions('No tienes pemisos para (listar) novedades turnos.');

        return false;                                                           
         
      }

     
      
      const response =  await dispatch( getNovedadesTurnoStore() );

      await validateResponse(response, ' (Listar  Novedades Turnos.) ');


    }



    const handleClick = (elem:any) => (event:any) => {
      setAnchorEl(event.currentTarget);
      setOpenElem(elem);
    };
  
    const handleClose = () => {
      setAnchorEl(null);
      setOpenElem(null);
    };
  
  
    const openDialogDetalleNovedadTurno = async(novedadTurnoId:string) => { 

      setAnchorEl(null);
      setOpenElem(null);
      
      const hasPermissions = await hasPemissions('modulo.thumano', 'modulo.novedades', 'view', 'modulo.novedadesturnos');
  
      if( !hasPermissions ){
  
        await validateLsPermissions('No tienes pemisos para (Ver) novedad turno.');

        return false;
  
      }
  

      const response =  await dispatch( storeDetailNovedadTurno(novedadTurnoId) );

      const result = await validateResponse(response, ' (Ver  Novedad Turno.) ');

      if ( !result ) {
         return false;
      }

      setModalDetalleNovedadTurno(true);


    }

    const openDialogEditarNovedadTurno = async(tipoNovedadId:string) => {


      setAnchorEl(null);
      setOpenElem(null);
      


      const hasPermissions = await hasPemissions('modulo.thumano', 'modulo.novedades', 'edit', 'modulo.novedadesturnos');


      if( !hasPermissions ){
  

        await validateLsPermissions('No tienes pemisos para (Editar) novedad turno.');

        return false;

                                                                    
      }

   
      await dispatch( storeDetailEditNovedadTurno(tipoNovedadId) );
      setModalEditarNovedadesTurnos(true);
      

    }
  
  

    const desactiveNovedadTurno = async(novedadTurnoId:string) => {


      setAnchorEl(null);
      setOpenElem(null);


      const hasPermissions = await hasPemissions('modulo.thumano', 'modulo.novedades', 'delete', 'modulo.tiponovedades');


      if( !hasPermissions ){
  
        await validateLsPermissions('No tienes pemisos para (Desactivar) novedad turno.');

        return false;

      }

        
      const response = await dispatch( storeDesActiveNovedadesTurno(novedadTurnoId) );

      await validateResponse(response, ' (Desactivar  Novedad Turno.) ');


  
    }
  
  
    const restoreNovedadTurno = async(novedadTurnoId:string) => {


      setAnchorEl(null);
      setOpenElem(null);


      const hasPermissions = await hasPemissions('modulo.thumano', 'modulo.novedades', 'restore', 'modulo.novedadesturnos');


      if( !hasPermissions ){
  
        await validateLsPermissions('No tienes pemisos para (Restaurar) novedad turno.');

        return false;
                                                                    
      }
  
  
      const response = await dispatch( storeActiveNovedadTurno(novedadTurnoId) );


      await validateResponse(response, ' (Restaurar  Novedad Turno.) ');

  
    }
  
    

    const handleCloseModalEditarNovedadesTurnos = () => {
      setModalEditarNovedadesTurnos(false);
    }

    const handleCloseModalDetalleNovedadTurno = () => {
      setModalDetalleNovedadTurno(false);
 }
  
   

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {

      setRowsPerPage(+event.target.value);

      setPage(0)

    };



    const handleChangePage = (event: unknown, newPage: number) => {

      setPage(newPage);

    };


    const changePaginationPage = async(event: React.ChangeEvent<unknown>, value: number) => {
            
        setPagePagination(value)
    
        const response =  await dispatch( getTipoNovedadByPagination(value) );
        
        
        if ( !response.ok && response.statusCode == 403 || response.code && response.code == 403) {
    
          setAlertSnack(true);
    
          await setMessageAlertSnack('No tienes permisos para ( listar ) clientes.');
    
          setTypeAlertSnack('error')
    
          return false;
    
        } 
    
    };
    

    const validateLsPermissions = async(message:string) => {

      const errors:any = {
        msg: message,
        status: 'error'
      };
    
      await setMsgSnack(errors);
      await setAlertSnack(true);
    
    
    }



    const validateResponse = async(response:any, textActionConcat: string) => {
        
        
          if (  response.code == 401  ) {
            await localStorage.removeItem('lastSelectedQypht');
            await closeSessionGlobal();
            setModalSessionExpired(true);
            return false;
          }
  
        
          // 403 NO TIENE PERMISSO PARA REALIZAR ESA ACCION 
          if (!response.ok && response.code == 403 ) { 
  
          
            const errors:any = {
              msg: response.errors[0]+' '+textActionConcat,
              status: 'error'
            };
          
            await setMsgSnack(errors);
            await setAlertSnack(true);
  
          
            return false;
            
          }
  
          
          // 400 ERROR EN DATOS ENVIADOS MAYORMENTE PROPIEDAD INVALIDA O MAL FORMATEADO 
          if (!response.ok && response.code == 400 ) { 
  
            const errors:any = [];
  
            response.errors.forEach( (element:any) => {
                
                errors.push({
                  type: 'error',
                  text: element
                })
            });
  
              
            setMessagesAlerts(errors);
            return false;
  
          }
  
          return true;
        
    }



    const checkEdit = async() => {
            
    
      const hasPermissionsEdit = await hasPemissions('modulo.thumano', 'modulo.novedades', 'edit', 'modulo.novedadesturnos');
    
      if ( hasPermissionsEdit ) {
            setCanEdit(true);
      }

    }
    
    
    const checkCanDelete = async() => {
        
        
        const havePermissions = await hasPemissions('modulo.thumano', 'modulo.novedades', 'delete', 'modulo.novedadesturnos');
        
        if ( havePermissions ) {
              setCanDelete(true);
        }
  
    }
    
    
    const checkRestore = async() => {
        
        const havePermissions = await hasPemissions('modulo.thumano', 'modulo.novedades', 'restore', 'modulo.novedadesturnos');
          
        if ( havePermissions ) {
              setCanRestore(true);
        }
  
    }



    return (
  
      <>
  
          {/* <MessagesDrawerRight data={msgErrors} title={''} /> */}
          <ModalSessionExpired  open={modalSessionExpired}  />

          <MultipleSnackbars messagesList={messagesAlerts} position={"bottom-left"} autoClose={timeAlert} />

          <SnackAlert open={alert_snack} onclick={setAlertSnack} text={msgSnack.msg} severity={msgSnack.status}/>

          <EditDialogNovedadesTurnos  open={modalEditarNovedadTurno} handleCloseModalEditarTipoNovedad={handleCloseModalEditarNovedadesTurnos} />
          <DetailDialogNovedadesTurnos  open={modalDetalleNovedadTurno} handleCloseModalDetalleNovedadTurno={handleCloseModalDetalleNovedadTurno} />
         
  
         {
              
              dataTableList.length > 0
                  
                ?
                <>

                <Card
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 3,
                    borderRadius: 2,
                    boxShadow: 3,
                    bgcolor: 'background.paper',
                    marginTop: paddingCardsTables
                  }}
                >
                  <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 700 }} aria-label="customized table">
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>#</StyledTableCell>
                          <StyledTableCell >Colaborador</StyledTableCell>
                          <StyledTableCell >Responsable</StyledTableCell>
                          <StyledTableCell >Tipo novedad</StyledTableCell>
                          <StyledTableCell >Descripcion</StyledTableCell>
                          <StyledTableCell >Fecha novedad</StyledTableCell>
                          <StyledTableCell >Hora novedad</StyledTableCell>
                          <StyledTableCell >Acciones</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dataTableList
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((novedadTurno:any, index:any) => (
                          <StyledTableRow key={index+1}>
                            <StyledTableCell component="th" scope="row">
                              {index+1}
                            </StyledTableCell>
                            <StyledTableCell component="th" scope="row">
                              { novedadTurno.empleado.name+ ' '+novedadTurno.empleado.last_name }
                            </StyledTableCell>
                            <StyledTableCell component="th" scope="row">
                              { novedadTurno.responsable.name+ ' '+novedadTurno.responsable.last_name }
                            </StyledTableCell>
                            <StyledTableCell >
                              { novedadTurno.tipoNovedad.name }
                            </StyledTableCell>
                            <StyledTableCell >
                              { novedadTurno.descripcion }
                            </StyledTableCell>
                            <StyledTableCell >
                              { formatDateToYmd(novedadTurno.fecha_novedad)  }
                            </StyledTableCell>
                            <StyledTableCell >
                              { novedadTurno.hora_novedad }
                            </StyledTableCell>
                            <StyledTableCell >
                              {
                                novedadTurno.status ? <Chip size='small' label="Activo" sx={{ color: '#5DCC0A', background: '#E4F6D6', fontWeight: 700 }} /> : <Chip size='small' label="Inactivo" sx={{ color: '#FF4C51', background: '#FFE2E3', fontWeight: 700 }} />
                              }
                            </StyledTableCell>
                            
                            <StyledTableCell >
                                  
                            
                            {
                            
                                <div> 
                                    <Button
                                      id={`demo-customized-button-${index+1}`}
                                      aria-controls={open ? `demo-customized-menu-${index+1}` : undefined}
                                      aria-haspopup="true"
                                      aria-expanded={open ? 'true' : undefined}
                                      variant="text"
                                      disableElevation
                                      onClick={handleClick(novedadTurno)}
                                      endIcon={<KeyboardArrowDownIcon />}
                                    >
                                      Acciones
                                    </Button>
                                    <StyledMenu
                                      id={`demo-customized-menu-${index+1}`}
                                      MenuListProps={{
                                        'aria-labelledby': `demo-customized-button-${index+1}`,
                                      }}
                                      anchorEl={anchorEl}
                                      open={openElem === novedadTurno}
                                      onClose={handleClose}
                                    >
                                      
                                      {
                                        (novedadTurno.status) 
                                          ? <div>
                                            <MenuItem onClick={ () => openDialogDetalleNovedadTurno(novedadTurno.id) }  disableRipple>
  
                                            <VisibilityIcon />
                                              Detalle 
                                            </MenuItem> 

                                              {
                                                 canEdit && 
                                                    <>
                                                        <Divider sx={{ my: 0.5 }} /> 
                                                        <MenuItem onClick={ () => openDialogEditarNovedadTurno(novedadTurno.id) }  disableRipple>
                                                         <EditIcon />
                                                           Editar
                                                        </MenuItem>
                                                    </>
                                              } 
                                          </div>
                                          :
                                          ''
                                      }
                                      
                                      {
                                        (novedadTurno.status) 
                                        ?
                                          <div>
                                            {
                                               canDelete && 
                                                 <>
                                                   <Divider sx={{ my: 0.5 }} />
                                                   <MenuItem onClick={ () => desactiveNovedadTurno(novedadTurno.id) } disableRipple>
                                                     <DeleteIcon />
                                                     Desactivar
                                                   </MenuItem>
                                                 </>
                                            }
                                          </div>
                                        :
                                        <div>
                                            {
                                               canRestore && 
                                                  <>
                                                      <Divider sx={{ my: 0.5 }} />
                                                      <MenuItem onClick={ () => restoreNovedadTurno(novedadTurno.id) } disableRipple>
                                                        <DeleteIcon />
                                                        Activar
                                                      </MenuItem>
                                                  </>
                                            }
                                        </div>
                                        
                                      }
                                      </StyledMenu>
                                  </div>
                        
                            }
            
            
            
            
                            </StyledTableCell>
                          </StyledTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
                <TablePagination
                  rowsPerPageOptions={[5, 10]}
                  labelRowsPerPage={'Filas por pagina'}
                  labelDisplayedRows={({ from, to, count }) => ` ${from}-${to} de  ${count} `}
                  component="div"
                  count={pagination.itemCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={ handleChangePage }
                  onRowsPerPageChange={ handleChangeRowsPerPage }
                />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginTop: 2,
                    marginBottom: 2
                  }}
                >
                <Pagination sx={{ justifyContent: 'center' }} color="primary" count={itemCountPagination} page={pagePagination} onChange={ changePaginationPage} showFirstButton showLastButton />
                </Box>

                </>


                
                :
                <Div>{"No hay novedades turnos disponibles..."}</Div>
         }
  
  
         
         
      </>
      
    );
}
